import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Namespace, useT, useDateFnsLocale } from "@racoon/i18n";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";
import { ViewSelector } from "../../../shared/components/viewSelector";
import { PickupLocationProgress } from "~/features/pickup-location-progress/pickup-location-progress";
import { DropdownSelector, type DropdownSelectorOption } from "~/ui/components/dropdownSelector";
import { TruckProgress } from "~/features/truck-progress/truck-progress";
import { useServiceProviderByIdQuery } from "~/shared/api/govDash/govDash.api";

type OrganizationDistrictFilterOption = DropdownSelectorOption;

type OrganizationDetailsProps = {
  /** Districts from gov geo access (e.g. `useGetGovGeoAccessQuery` on the records page). */
  districtOptions?: OrganizationDistrictFilterOption[];
  selectedRecordId: string | undefined;
};

export const OrganizationDetails = ({
  districtOptions = [],
  selectedRecordId,
}: OrganizationDetailsProps) => {
  const t = useT();
  const [activeSection, setActiveSection] = useState<"general" | "summary">("general");

  const { data: serviceProviderData } = useServiceProviderByIdQuery({
    id: selectedRecordId ? BigInt(selectedRecordId) : undefined,
  });

  const jurRows = useMemo(
    () => [
      { label: t(Namespace.Govdash, "bin"), value: serviceProviderData?.organization?.bin },
      // {
      //   label: t(Namespace.Govdash, "jurAddress"),
      //   value: "Republic of Kazakhstan, Almaty, Kabanbay Batyr 453",
      // },
      // { label: t(Namespace.Govdash, "email"), value: "contact@organization.kz" },
      // { label: t(Namespace.Govdash, "phone"), value: "+7 (777) 000-00-00" },
    ],
    [t, serviceProviderData],
  );
  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
      ] as const,
    [t],
  );

  const operationalRows = useMemo(
    () => [
      {
        label: t(Namespace.Govdash, "depotsCount"),
        value: serviceProviderData?.affiliatedDepots?.toString() || "-",
      },
      {
        label: t(Namespace.Govdash, "totalAutopark"),
        value:
          serviceProviderData?.affiliatedTrucks != null
            ? `${serviceProviderData.affiliatedTrucks} ${t(Namespace.Govdash, "vehicles")}`
            : "-",
      },
      {
        label: t(Namespace.Govdash, "affiliatedGarbageGenerators"),
        value: serviceProviderData?.affiliatedReceivers?.toString() || "-",
        shouldWrap: false,
      },
      {
        label: t(Namespace.Govdash, "servicedPickupLocations"),
        value: serviceProviderData?.affiliatedPickupLocations?.toString() || "-",
        shouldWrap: false,
      },
    ],
    [t, serviceProviderData],
  );

  return (
    <CardContentWrapper hasDivider={false} className="flex min-h-0 flex-col overflow-hidden">
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>
      <div className="overflow-y-auto min-h-0 flex-1">
        {activeSection === "general" && (
          <>
            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "juridicalInfo")}
              </p>
              <CardContentWrapper>
                {jurRows.map((row) => (
                  <InfoRow key={row.label} label={row.label} value={row.value} />
                ))}
              </CardContentWrapper>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-base font-semibold tracking-tight text-slate-900">
                {t(Namespace.Govdash, "operationalInfo")}
              </p>
              <CardContentWrapper>
                {operationalRows.map((row) => (
                  <InfoRow
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    shouldWrap={row.shouldWrap ?? true}
                  />
                ))}
              </CardContentWrapper>
            </div>
          </>
        )}
        {activeSection === "summary" && (
          <OperationalSummary
            districtOptions={districtOptions}
            selectedRecordId={selectedRecordId}
          />
        )}
      </div>
    </CardContentWrapper>
  );
};

const OperationalSummary = ({
  districtOptions,
  selectedRecordId,
}: {
  districtOptions: OrganizationDistrictFilterOption[];
  selectedRecordId: string | undefined;
}) => {
  const t = useT();
  const [selectedDistrictValues, setSelectedDistrictValues] = useState<string[]>([]);

  const dateFnsLocale = useDateFnsLocale();
  const todayLabel = useMemo(() => {
    const [day, month, year] = format(new Date(), "d MMM yyyy", { locale: dateFnsLocale })
      .replace(".", "")
      .split(" ");
    return `${day} ${month.charAt(0).toUpperCase()}${month.slice(1)} ${year}`;
  }, [dateFnsLocale]);

  const districtTriggerLabel = useMemo(() => {
    if (selectedDistrictValues.length === 0) {
      return t(Namespace.Govdash, "allDistricts");
    }
    const labels = selectedDistrictValues
      .map((v) => districtOptions.find((o) => o.value === v)?.label)
      .filter(Boolean) as string[];
    if (labels.length === 1) {
      return labels[0];
    }
    return labels.slice(0, 2).join(", ") + (labels.length > 2 ? "…" : "");
  }, [districtOptions, selectedDistrictValues, t]);

  useEffect(() => {
    const allowed = new Set(districtOptions.map((o) => o.value));
    setSelectedDistrictValues((prev) => prev.filter((v) => allowed.has(v)));
  }, [districtOptions]);

  // INFO: currently waste status is set for today, provider-scoped
  const districtOrganizationIdsBigInt = selectedDistrictValues.map((v) => BigInt(v));
  const serviceProviderId = selectedRecordId ? BigInt(selectedRecordId) : undefined;

  return (
    <>
      <div className="p-4 flex items-center justify-between">
        <p className="text-base font-semibold tracking-tight text-slate-900">
          {t(Namespace.Govdash, "operationalSummary")}
        </p>
        <p className="text-sm font-medium tracking-tight text-text-secondary">{todayLabel}</p>
      </div>
      <div className="border-y border-slate-200 p-4">
        <DropdownSelector
          triggerLabel={districtTriggerLabel}
          multiple
          options={districtOptions}
          values={selectedDistrictValues}
          onApply={setSelectedDistrictValues}
          sectionTitle={t(Namespace.Common, "objectsFilterLabel")}
          emptyMessage={t(Namespace.Govdash, "districtFilterEmpty" as any)}
          resetLabel={t(Namespace.Common, "reset")}
        />
      </div>
      <PickupLocationProgress
        serviceProviderId={serviceProviderId}
        districtOrganizationIds={districtOrganizationIdsBigInt}
      />
      <TruckProgress
        serviceProviderId={serviceProviderId}
        districtOrganizationIds={districtOrganizationIdsBigInt}
      />
    </>
  );
};
